<template>
  <div v-if="show" class="analysis-popup-overlay" data-testid="analysis-popup" :class="{ 'overlay-compact': isCompactState, 'is-error': isErrorState }" role="dialog" aria-modal="true">
    <div class="analysis-popup" :class="{ 'popup-compact': isCompactState, 'popup-reasons': askingReason }">

      <!-- ====== LOADING STATE ====== -->
      <div v-if="loading || loadingImages.length > 0" class="state-center">
        <i class="pi pi-spin pi-spinner loading-spinner" data-testid="analysis-loading-spinner" aria-hidden="true"></i>
        <h2 class="popup-title" data-testid="analysis-popup-title">{{ $t('message.analyzing') }}</h2>

        <template v-if="loadingImages.length > 0">
          <hr class="divider">
          <h3 class="upload-count">{{ $t('message.imagesLeftToUpload') }}: {{ loadingImages.length }}</h3>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: `${averageUploadProgress}%` }"></div>
          </div>
          <div v-if="uploadTakingTooLong" class="warning-box">
            <BzIcon v-if="systemLane" name="info" class="warning-icon" />
            <i v-else class="pi pi-info-circle warning-icon"></i>
            <p class="warning-text">{{ $t('message.uploadTakingTooLong') }}</p>
          </div>
          <button class="btn btn-ghost" @click="$emit('background-analysis', { asked: aiWrongEnabled })">
            {{ $t('message.continueInBackground') }}
          </button>
        </template>

        <template v-else-if="analysisTakingTooLong">
          <hr class="divider">
          <div class="warning-box">
            <BzIcon v-if="systemLane" name="info" class="warning-icon" />
            <i v-else class="pi pi-info-circle warning-icon"></i>
            <p class="warning-text">{{ $t('message.analysisTakingTooLong') }}</p>
          </div>
          <button class="btn btn-ghost" @click="$emit('background-analysis', { asked: aiWrongEnabled })">
            {{ $t('message.continueInBackground') }}
          </button>
        </template>
      </div>

      <!-- ====== ERROR STATE ====== -->
      <div v-else-if="error" class="state-center">
        <BzIcon v-if="systemLane" name="error" class="state-icon state-icon--error" />
        <img v-else src="@/assets/error_outline.svg" class="state-icon"/>
        <h2 class="popup-title" data-testid="analysis-popup-title">{{ $t('message.analysisError') }}</h2>
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-primary" @click="$emit('close', { asked: aiWrongEnabled })">{{ $t('message.close') }}</button>
      </div>

      <!-- ====== RESULTS ====== -->
      <template v-else-if="analysisResult && analysisResult.missingItems !== null">

        <!-- MISSING ITEMS -->
        <template v-if="analysisResult.missingItems.length > 0">

          <!-- Alert header — icon + text -->
          <div class="alert-header">
            <BzIcon v-if="systemLane" name="error" class="alert-icon" />
            <i v-else class="pi pi-exclamation-circle alert-icon"></i>
            <div class="alert-text">
              <h2 class="alert-title" data-testid="analysis-alert-title">
                {{ $t('message.notDetected', totalMissingItems, { count: totalMissingItems }) }}
              </h2>
              <p v-if="!askingReason" class="alert-subtitle">{{ $t('message.notDetectedSubtitleSingle') }}</p>
              <p v-else class="alert-summary" data-testid="reason-items" translate="no">{{ missingItemsSummary }}</p>
            </div>
          </div>

          <!-- Scrollable category → product list -->
          <div v-if="!askingReason" class="items-wrapper" :class="{ 'has-more': showScrollHint }">
            <div class="items-list" ref="itemsList" @scroll="onItemsScroll">
              <div
                v-for="(item, index) in analysisResult.missingItems"
                :key="index"
                class="missing-item"
                data-testid="missing-item"
              >
                <div class="item-header">
                  <span v-if="item.icon" class="item-emoji" v-html="item.icon"></span>
                  <span v-else class="item-emoji">📦</span>
                  <span class="item-name" data-testid="missing-item-name" translate="no">{{ item.groupName }}</span>
                  <span class="item-qty" data-testid="missing-item-qty">{{ $t('message.missingCount', item.missingQuantity, { count: item.missingQuantity }) }}</span>
                </div>
                <div v-if="item.name && item.name.length > 0" class="item-details">
                  <span
                    v-for="(subItem, si) in item.name"
                    :key="si"
                    class="sub-item-text"
                    data-testid="missing-sub-item"
                    translate="no"
                  >{{ subItem }}</span>
                </div>
                <div v-if="item.name && item.name.length > 1 && item.name.length > item.missingQuantity" class="item-info-hint" data-testid="missing-item-hint">
                  <BzIcon v-if="systemLane" name="info" class="item-info-icon" />
                  <i v-else class="pi pi-info-circle item-info-icon"></i>
                  <span class="item-info-text" data-testid="missing-item-hint-text">{{ $t('message.itemNotDetectedInPhoto', item.missingQuantity, { count: item.missingQuantity }) }}</span>
                </div>
              </div>
            </div>
            <div v-if="showScrollHint" class="scroll-hint">
              ↓ {{ overflowCount }} {{ $t('message.more') }}
            </div>
          </div>

          <!-- CTAs -->
          <div v-if="!askingReason" class="cta-container" data-testid="analysis-cta">
            <button class="btn btn-primary btn-glow" data-testid="analysis-retake" @click="$emit('add-photos', { asked: aiWrongEnabled })">
              <BzIcon v-if="systemLane" name="photo_camera" class="btn-icon" />
              <i v-else class="pi pi-camera btn-icon"></i>
              {{ $t('message.addPhotos') }}
            </button>
            <button class="btn btn-secondary" data-testid="analysis-continue" @click="executeContinue">
              <span class="btn-secondary-label">{{ $t('message.stillContinue') }}</span>
              <span class="btn-secondary-warning">{{ $t('message.stillContinueWarning') }}</span>
            </button>
          </div>

          <!-- Entregar sin corregir tiene una segunda pregunta: sin ella, el 9 % de pedidos que
               pasa por aquí no dice si fue fallo del modelo, packaging o falta de stock, que es lo
               único que indica qué arreglar. Tres respuestas, un toque, y el mensaje sale solo. -->
          <div v-else class="reason-container" data-testid="ai-wrong-reasons">
            <!-- Volver arriba a la izquierda: un toque de más en "entregar sin corregir" no puede
                 dejar sin salida a quien en realidad quería corregir. -->
            <div class="reason-head">
              <button
                class="reason-back"
                data-testid="ai-wrong-back"
                :aria-label="$t('message.aiWrongBack')"
                :title="$t('message.aiWrongBack')"
                @click="backFromReason"
              >
                <BzIcon v-if="systemLane" name="arrow_back" />
                <i v-else class="pi pi-arrow-left"></i>
              </button>
              <p class="reason-title">{{ $t('message.aiWrongTitle', totalMissingItems, { count: totalMissingItems }) }}</p>
            </div>
            <button
              v-for="reason in aiWrongReasons"
              :key="reason.id"
              class="btn btn-reason"
              :data-testid="`ai-wrong-${reason.id}`"
              @click="pickReason(reason.id)"
            >
              <span class="reason-chip"><BzIcon v-if="systemLane" :name="reasonIcon(reason.icon)" /><i v-else class="pi" :class="reason.icon"></i></span>
              <span>{{ $t(reason.labelKey) }}</span>
            </button>
            <button class="btn btn-reason btn-reason--skip" data-testid="ai-wrong-skip" @click="pickReason(null)">
              <BzIcon v-if="systemLane" name="close" />
              <i v-else class="pi pi-times"></i>
              <span>{{ $t('message.aiWrongSkip') }}</span>
            </button>
          </div>

        </template>

        <!-- PERFECT ORDER -->
        <div v-else class="state-center">
          <BzIcon v-if="systemLane" name="check_circle" class="state-icon icon-lg state-icon--ok" />
          <img v-else src="@/assets/check_circle_outline.svg" class="state-icon icon-lg"/>
          <h2 class="popup-title title-success" data-testid="analysis-popup-title" data-state="success">{{ $t('message.perfect') }}</h2>
          <div class="progress-bar progress-bar-success">
            <div ref="progress" :class="['progress-fill fill-success', { 'progress-animate': showProgressBar }]"></div>
          </div>
        </div>

      </template>
    </div>

    <audio ref="correctSound" src="/correct.mp3" preload="auto"></audio>
    <audio ref="errorSound" src="/error.mp3" preload="auto"></audio>
  </div>
</template>

<script>
import Logger from '@/logger';
import posthog from 'posthog-js';
import { AI_WRONG_REASONS, isAiWrongReasonsEnabled, isReasonTapSettling } from '@/aiWrongFlow';
import BzIcon from '../ui/components/BzIcon.vue';
import { systemLane } from '../ui/lane';

/** The reasons' PrimeIcons (src/aiWrongFlow.js) as the design system's Material Symbols. */
const REASON_ICONS = { 'pi-eye-slash': 'visibility_off', 'pi-box': 'inventory_2', 'pi-ban': 'block' };

const logger = new Logger({screen: 'AnalysisPopupComponent'});

export default {
  name: 'AnalysisPopupComponent',
  components: { BzIcon },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    },
    loadingImages: {
      type: Array,
      default: () => []
    },
    analysisResult: {
      type: Object,
      default: null
    },
    error: {
      type: String,
      default: null
    },
    uploadTakingTooLong: {
      type: Boolean,
      default: false
    },
    analysisTakingTooLong: {
      type: Boolean,
      default: false
    }
  },
  emits: ['continue-order', 'add-photos', 'close', 'background-analysis'],
  data() {
    return {
      showProgressBar: false,
      // Peak number of images this upload batch ever had queued. The bar is computed against
      // this total (not the shrinking remaining set), so it fills as photos complete instead
      // of emptying each time one leaves the queue.
      uploadPeakCount: 0,
      // Scroll indicator
      hasItemsOverflow: false,
      isScrolledToBottom: false,
      overflowCount: 0,
      // Segunda pregunta de "entregar sin corregir". Piloto: se abre por locales, no a toda la
      // flota, porque cambia el gesto con el que la cocina cierra un pedido incompleto. El
      // override de localStorage es para poder probarlo en una tablet concreta sin tocar el flag,
      // igual que `bronze_help_send_direct`.
      // Se resuelve al ABRIR el modal, no aquí: este componente se monta al arrancar la app, y
      // en ese instante PostHog puede no haber traído todavía los flags ni saber a qué restaurante
      // pertenece la tablet (`setPersonPropertiesForFlags` corre después del login). Leerlo en
      // `data()` congelaba un `false` para toda la sesión — el piloto se habría quedado apagado en
      // los restaurantes del brazo de tratamiento sin que nada lo dijera, que es la peor forma de
      // arruinar un experimento: sin ruido.
      aiWrongEnabled: false,
      askingReason: false,
      askingReasonAt: null,
      aiWrongReasons: AI_WRONG_REASONS,
      // The service screen this page was painted with (src/ui/lane.js): fixed for the page's life.
      systemLane: systemLane()
    };
  },
  computed: {
    totalMissingItems() {
      if (!this.analysisResult || !this.analysisResult.missingItems) return 0;
      return this.analysisResult.missingItems
        .reduce((total, item) => total + (item.missingQuantity || 0), 0);
    },
    showScrollHint() {
      return this.hasItemsOverflow && !this.isScrolledToBottom && this.overflowCount > 0;
    },
    /** Los productos que faltan, en una línea. Con muchos se corta con puntos suspensivos. */
    missingItemsSummary() {
      return (this.analysisResult?.missingItems || [])
        .map((item) => (item.missingQuantity > 1 ? `${item.groupName} ×${item.missingQuantity}` : item.groupName))
        .join(' · ');
    },
    /** The error state: what the template shows when nothing is loading and there is an error. */
    isErrorState() {
      return !this.loading && this.loadingImages.length === 0 && Boolean(this.error);
    },
    isCompactState() {
      if (this.loading || this.loadingImages.length > 0 || this.error) return true;
      if (this.analysisResult && this.analysisResult.missingItems !== null && this.analysisResult.missingItems.length === 0) return true;
      return false;
    },
    averageUploadProgress() {
      const remaining = this.loadingImages.length;
      const total = Math.max(this.uploadPeakCount, remaining);
      if (!total) return 0;
      // Fraction uploaded of the images STILL in the queue (0..1 each).
      const partial = this.loadingImages.reduce((sum, image) => {
        return sum + (image.totalBytes > 0
          ? Math.min(1, image.transferredBytes / image.totalBytes)
          : 0);
      }, 0);
      // Images that already finished and left the queue count as fully done. Progress over the
      // full batch rises monotonically instead of resetting each time a photo completes.
      const completed = total - remaining;
      return Math.min(100, Math.round(((completed + partial) / total) * 100));
    },
  },
  watch: {
    loadingImages: {
      handler(imgs) {
        const n = imgs?.length || 0;
        if (n === 0) { this.uploadPeakCount = 0; return; } // batch done/reset — clear the peak
        if (n > this.uploadPeakCount) this.uploadPeakCount = n;
      },
      immediate: true,
    },
    analysisResult: {
      handler(newVal) {
        this.askingReason = false;
        if (newVal !== null) {
          if (newVal.missingItems !== null && newVal.missingItems.length === 0) {
            this.playCorrectSound();
            this.animateProgressBar();
          } else if (newVal.missingItems !== null && newVal.missingItems.length > 0) {
            this.playErrorSound();
            this.$nextTick(() => {
              this.checkOverflow();
              setTimeout(() => this.checkOverflow(), 200);
            });
          }
        }
      },
      immediate: true
    },
    show(newVal) {
      if (newVal) {
        // Al abrir: el brazo se decide con lo que PostHog sabe AHORA, no con lo que sabía al
        // arrancar. Es también lo que permite que añadir un restaurante al flag surta efecto sin
        // reiniciar la tablet.
        this.aiWrongEnabled = isAiWrongReasonsEnabled(posthog, typeof localStorage !== 'undefined' ? localStorage : null);
      }
      if (!newVal) {
        this.hasItemsOverflow = false;
        this.isScrolledToBottom = false;
        this.overflowCount = 0;
        // Un popup que se abre de nuevo nunca arranca en la segunda pregunta.
        this.askingReason = false;
      }
    }
  },
  methods: {
    reasonIcon(pi) {
      return REASON_ICONS[pi] || 'help';
    },
    executeContinue() {
      // Con el piloto activo, entregar sin corregir no cierra: pregunta por qué. Sin él, el flujo
      // es exactamente el de antes.
      if (this.aiWrongEnabled) {
        this.askingReason = true;
        // Cuándo empieza a correr la pregunta. `shown_ms` mide desde que se pintó el veredicto e
        // incluye, por construcción, lo que se tarda en contestar: si no se separan, el guardarraíl
        // del coste salta por el propio diseño y no distingue "duda en el paso 1" de "la pregunta
        // cuesta segundos".
        this.askingReasonAt = Date.now();
        return;
      }
      this.$emit('continue-order', { asked: false });
    },
    reasonStepSettling() {
      return isReasonTapSettling(this.askingReasonAt);
    },
    backFromReason() {
      // Volver no puede dejar sin salida a quien quería corregir, pero un toque a <350 ms de
      // pintarse el paso es el rebote del toque anterior, no una decisión de volver.
      if (this.reasonStepSettling()) return;
      this.askingReason = false;
    },
    pickReason(reasonId) {
      // Un toque a <350 ms de pintarse el paso es el rebote del toque de "entregar sin corregir",
      // no una respuesta: descartarlo, o contaminaría el reparto de motivos.
      if (this.reasonStepSettling()) return;
      // Se entrega igual en los cuatro casos, incluido "Cerrar": la pregunta no puede convertirse
      // en un peaje para poder entregar el pedido.
      //
      // NO se baja `askingReason` aquí: bajarlo repintaba el paso anterior (los dos botones) en el
      // fotograma previo a que el popup se cerrase, y se veía un salto atrás de un instante. Lo
      // resetea el watcher de `show` cuando el popup ya está oculto.
      this.$emit('continue-order', {
        reason: reasonId || null,
        asked: true,
        reason_ms: this.askingReasonAt ? Date.now() - this.askingReasonAt : null,
      });
    },

    // Scroll indicator
    checkOverflow() {
      const el = this.$refs.itemsList;
      if (el) {
        this.hasItemsOverflow = el.scrollHeight > el.clientHeight + 10;
        this.countHiddenItems(el);
      }
    },
    countHiddenItems(el) {
      const items = el.querySelectorAll('.missing-item');
      const wrapperBottom = el.parentElement.getBoundingClientRect().bottom;
      const gradientHeight = 40;
      let hidden = 0;
      items.forEach(item => {
        if (item.getBoundingClientRect().top >= wrapperBottom - gradientHeight) {
          hidden++;
        }
      });
      this.overflowCount = hidden;
    },
    onItemsScroll(e) {
      const el = e.target;
      this.isScrolledToBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
      this.countHiddenItems(el);
    },

    // Progress & sounds
    animateProgressBar() {
      this.$nextTick(() => {
        this.showProgressBar = true;
        setTimeout(() => {
          this.$emit('close', { asked: this.aiWrongEnabled });
        }, 2000);
      });
    },
    playSound(soundRef) {
      const sound = this.$refs[soundRef];
      if (sound) {
        sound.load();
        sound.play().catch(error => {
          logger.error(`Error playing ${soundRef} sound`, {error: error});
          if ('Audio' in window) {
            new Audio(sound.src).play().then(() => {
              logger.info(`Fallback audio playing ${soundRef} playback successful`);
            }).catch(fallbackError => {
              logger.error(`Fallback audio playback playing ${soundRef} failed`, {error: fallbackError});
            });
          }
        });
      }
    },
    playCorrectSound() {
      this.playSound('correctSound');
    },
    playErrorSound() {
      this.playSound('errorSound');
    }
  }
};
</script>

<style scoped>
/* ========================================
   OVERLAY
   ======================================== */
.analysis-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--bz-p-black-a60);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: var(--bz-z-lane-popup);
}

@media (min-width: 768px) {
  .analysis-popup-overlay {
    align-items: center;
  }
}

/* ========================================
   MODAL
   ======================================== */
.analysis-popup {
  background: var(--bz-p-white);
  color: var(--bz-p-1a1a2e);
  padding: 28px 20px 16px;
  border-radius: 20px 20px 0 0;
  width: 100%;
  height: calc(100dvh - 72px);
  max-height: calc(100dvh - 72px);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  overflow: hidden;
  font-family: "DM Sans", sans-serif;
}

@media (min-width: 768px) {
  .analysis-popup {
    padding: 24px;
    border-radius: 20px;
    width: min(85vw, 400px);
    height: auto;
    max-height: 92vh;
  }
  /* El paso de motivos es más ancho, y SOLO él: las cuatro respuestas son frases, no etiquetas,
     y a 420 px de contenido las dos largas caían a dos líneas (medido: la más larga necesita
     373 px y el carril de texto daba 333). Con 500 px de contenido caben de una línea en los tres
     idiomas. Sin transición de ancho a propósito: animar layout en la tablet de cien euros es lo
     que veta la norma 1, y el salto coincide con el cambio de contenido, así que se lee como
     "otro paso". */
  .analysis-popup.popup-reasons {
    width: min(85vw, 500px);
  }
}

@media (min-width: 1024px) {
  .analysis-popup {
    width: min(80vw, 420px);
    max-height: 88vh;
  }
  .analysis-popup.popup-reasons {
    width: min(80vw, 500px);
  }
}

/* ========================================
   STATE CENTER — loading, error, perfect
   ======================================== */
.state-center {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  text-align: center;
  min-height: 0;
}

/* ========================================
   SHARED ELEMENTS
   ======================================== */
.state-icon {
  width: 56px;
  height: 56px;
  margin-bottom: 12px;
}

.icon-lg {
  width: 56px;
  height: 56px;
}

@media (min-width: 768px) {
  .state-icon {
    width: 64px;
    height: 64px;
  }
  .icon-lg {
    width: 72px;
    height: 72px;
  }
}

.popup-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: var(--bz-p-1a1a2e);
}

@media (min-width: 768px) {
  .popup-title {
    font-size: 24px;
  }
}

.title-success {
  color: var(--bz-p-green-600);
  font-size: 22px;
  margin-top: 4px;
}

@media (min-width: 768px) {
  .title-success {
    font-size: 28px;
  }
}

.divider {
  border: none;
  height: 1px;
  background: var(--bz-p-gray-200);
  margin: 16px 0;
  width: 100%;
}

/* ========================================
   PROGRESS BAR
   ======================================== */
.progress-bar {
  width: 100%;
  height: 12px;
  background: var(--bz-p-gray-200);
  border-radius: 6px;
  overflow: hidden;
  margin: 12px 0;
}

.progress-fill {
  height: 100%;
  background: var(--bz-p-blue-600);
  border-radius: 6px;
  transition: width 0.3s ease;
}

.progress-bar-success {
  margin-top: 16px;
  height: 8px;
}

.fill-success {
  background: var(--bz-p-green-600);
  width: 0;
}

@keyframes fillProgress {
  from { width: 0; }
  to { width: 100%; }
}

.progress-animate {
  animation: fillProgress 2s ease-out forwards;
}

/* ========================================
   BUTTONS — shared
   ======================================== */
.btn {
  width: 100%;
  border: none;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  border-radius: 12px;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  touch-action: manipulation;
}

@media (prefers-reduced-motion: no-preference) {
  .btn {
    transition: transform 0.1s ease;
  }
}

.btn:active {
  transform: scale(0.97);
}

/* Primary CTA */
.btn-primary {
  height: 56px;
  font-size: 17px;
  font-weight: 700;
  background: var(--bz-p-blue-600);
  color: var(--bz-p-white);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.btn-icon {
  font-size: 20px;
}

@media (min-width: 768px) {
  .btn-icon {
    font-size: 26px;
  }
  /* 72 px de alto y 22 px de cuerpo: en la tablet del pase se pulsa con guantes y se lee de pie,
     y estos dos botones son la decisión del pedido. Los 58/18 anteriores venían del móvil. */
  .btn-primary {
    height: 72px;
    font-size: 22px;
  }
}

/* The retake button no longer pulses (`btn-glow`): norm 1, nothing loops beside the photo
   (docs/sistema-diseno/forma-y-tamano.md, step 1). Its blue and its place already make it the answer. */

/* Secondary CTA — with warning sublabel */
.btn-secondary {
  height: 56px;
  font-size: 15px;
  font-weight: 500;
  background: none;
  color: var(--bz-p-gray-700);
  border: 1.5px solid var(--bz-p-gray-300);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
}

.btn-secondary-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--bz-p-gray-700);
}

.btn-secondary-warning {
  font-size: 11px;
  font-weight: 400;
  color: var(--bz-p-gray-400);
}

@media (min-width: 768px) {
  .btn-secondary {
    height: 72px;
  }
  .btn-secondary-label {
    font-size: 20px;
  }
  .btn-secondary-warning {
    font-size: 14px;
  }
}

/* Ghost — used in loading state */
.btn-ghost {
  height: 56px;
  font-size: 16px;
  font-weight: 700;
  background: none;
  color: var(--bz-p-gray-500);
  border: 1px solid var(--bz-p-gray-300);
}

@media (min-width: 768px) {
  .btn-ghost {
    height: 64px;
    font-size: 18px;
  }
}

/* ========================================
   LOADING STATE
   ======================================== */
.loading-spinner {
  font-size: 36px;
  color: var(--bz-p-blue-600);
  margin-bottom: 12px;
}

@media (min-width: 768px) {
  .loading-spinner {
    font-size: 40px;
  }
}

.upload-count {
  font-size: 24px;
  font-weight: 500;
  margin: 0 0 12px 0;
  line-height: 1.4;
  color: var(--bz-p-1a1a2e);
}

@media (min-width: 768px) {
  .upload-count {
    font-size: 28px;
  }
}

.warning-box {
  margin: 16px 0;
  background: var(--bz-surface--slate-100);
  border: 1px solid var(--bz-p-slate-200);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.warning-icon {
  font-size: 28px;
  color: var(--bz-p-slate-500);
  margin-bottom: 8px;
}

@media (min-width: 768px) {
  .warning-icon {
    font-size: 32px;
  }
}

.warning-text {
  font-size: 16px;
  color: var(--bz-p-slate-600);
  margin: 0;
  line-height: 1.4;
  text-align: center;
  white-space: pre-line;
}

@media (min-width: 768px) {
  .warning-text {
    font-size: 18px;
  }
}

/* ========================================
   ERROR STATE
   ======================================== */
.error-message {
  font-size: 18px;
  color: var(--bz-p-red-600);
  margin: 12px 0 24px 0;
  line-height: 1.4;
}

@media (min-width: 768px) {
  .error-message {
    font-size: 20px;
  }
}

/* ========================================
   ALERT HEADER — horizontal layout
   ======================================== */
.alert-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 4px 0 24px 0;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .alert-header {
    padding: 0 0 28px 0;
  }
}

.alert-icon {
  font-size: 36px;
  color: var(--bz-p-red-600);
  flex-shrink: 0;
  line-height: 1;
}

@media (min-width: 768px) {
  .alert-icon {
    font-size: 40px;
  }
}

.alert-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.alert-title {
  font-size: 21px;
  font-weight: 700;
  color: var(--bz-p-red-600);
  line-height: 1.2;
  margin: 0;
  text-align: left;
}

@media (min-width: 768px) {
  .alert-title {
    font-size: 22px;
  }
}

.alert-subtitle {
  font-size: 15px;
  font-weight: 400;
  color: var(--bz-p-gray-500);
  margin: 0;
  line-height: 1.3;
  text-align: left;
}

/* Los productos que faltan, bajo el «Falta 1 producto»: cuánto y qué en un solo bloque. Ocupa el
   sitio del subtítulo, que en este paso ya no aplica. Con muchos, se corta con puntos suspensivos:
   el detalle acaba de estar en pantalla y el total sigue arriba. */
.alert-summary {
  font-size: 17px;
  font-weight: 600;
  color: var(--bz-p-gray-700);
  margin: 0;
  line-height: 1.3;
  text-align: left;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (min-width: 768px) {
  .alert-subtitle {
    font-size: 15px;
  }
}

/* ========================================
   ITEMS LIST
   ======================================== */
.items-wrapper {
  flex: 1;
  min-height: 0;
  width: calc(100% + 8px);
  margin-right: -8px;
  position: relative;
  overflow: hidden;
}

/* Al preguntar el motivo la lista no debe seguir estirándose: `flex: 1` la hacía ocupar todo el
   alto libre y dejaba una franja en blanco entre los productos y la pregunta. Aquí se encoge a su
   contenido, con un techo para que una lista larga siga pudiendo hacer scroll. */
/* Los productos que faltan, en una línea, en el paso de motivos. Sustituye a la lista completa:
   con la cabecera y tres botones grandes no quedan más de ~100 px, y ahí la lista se recortaba a
   media fila dejando productos invisibles y sin scroll (el `max-height` en porcentaje del scroller
   no resuelve dentro de un flex de altura indefinida). Una línea cabe siempre y no esconde nada. */
.items-wrapper::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(to bottom, var(--bz-p-white-a0), var(--bz-p-white));
  pointer-events: none;
  z-index: 1;
}

.items-list {
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  max-height: 100%;
  padding-right: 8px;
  padding-bottom: 44px;
  scrollbar-width: thin;
  scrollbar-color: var(--bz-p-gray-300) transparent;
}

.items-list::-webkit-scrollbar {
  width: 4px;
}

.items-list::-webkit-scrollbar-track {
  background: transparent;
}

.items-list::-webkit-scrollbar-thumb {
  background: var(--bz-p-gray-300);
  border-radius: 2px;
}

@media (min-width: 768px) {
  .items-list {
    max-height: min(50vh, 400px);
    padding-bottom: 48px;
  }
}

/* Each category card */
.missing-item {
  border: 1.5px solid var(--bz-p-red-300);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 10px;
  background: var(--bz-p-red-50);
}

@media (min-width: 768px) {
  .missing-item {
    margin-bottom: 12px;
  }
}

/* Category row */
.item-header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.item-emoji {
  font-size: 24px;
  line-height: 1;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .item-emoji {
    font-size: 26px;
  }
}

.item-emoji :deep(img) {
  width: 24px;
  height: 24px;
  vertical-align: middle;
}

@media (min-width: 768px) {
  .item-emoji :deep(img) {
    width: 26px;
    height: 26px;
  }
}

.item-name {
  flex: 1;
  font-size: 18px;
  font-weight: 700;
  color: var(--bz-p-zinc-900);
  text-align: left;
  line-height: 1.3;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: capitalize;
}

@media (min-width: 768px) {
  .item-name {
    font-size: 19px;
  }
}

.item-qty {
  font-size: 15px;
  font-weight: 700;
  background: var(--bz-p-red-600);
  color: var(--bz-p-white);
  padding: 5px 12px;
  border-radius: 20px;
  line-height: 1.2;
  white-space: nowrap;
  flex-shrink: 0;
  letter-spacing: 0.2px;
}

@media (min-width: 768px) {
  .item-qty {
    font-size: 16px;
    padding: 6px 14px;
  }
}

/* Sub-items (specific products) — aligned with category name */
.item-details {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 6px;
  padding-left: 34px;
}

@media (min-width: 768px) {
  .item-details {
    padding-left: 36px;
  }
}

.sub-item-text {
  font-size: 16px;
  font-weight: 400;
  color: var(--bz-p-zinc-900);
  text-align: left;
  line-height: 1.5;
}

@media (min-width: 768px) {
  .sub-item-text {
    font-size: 17px;
  }
}

/* Info hint — ambiguous detection */
.item-info-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
  padding-left: 34px;
}

@media (min-width: 768px) {
  .item-info-hint {
    padding-left: 36px;
  }
}

.item-info-icon {
  font-size: 14px;
  color: var(--bz-p-red-600);
  flex-shrink: 0;
  margin-top: 2px;
}

.item-info-text {
  font-size: 13px;
  font-weight: 400;
  color: var(--bz-p-red-600);
  text-align: left;
  line-height: 1.4;
}

@media (min-width: 768px) {
  .item-info-icon {
    font-size: 15px;
  }
  .item-info-text {
    font-size: 14px;
  }
}

/* Scroll hint */
.scroll-hint {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--bz-p-white);
  border: 1px solid var(--bz-p-gray-200);
  border-radius: 20px;
  padding: 4px 14px;
  color: var(--bz-p-gray-500);
  font-size: 13px;
  font-weight: 600;
  pointer-events: none;
  z-index: 2;
  white-space: nowrap;
  box-shadow: 0 2px 8px var(--bz-p-black-a8);
}

/* ========================================
   CTA CONTAINER
   ======================================== */
.cta-container {
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding-top: 20px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0));
}

/* Segunda pregunta de "entregar sin corregir": mismo sitio y mismo lenguaje que los CTA a los que
   sustituye, para que se lea como el siguiente paso del mismo modal y no como otra pantalla. */
.reason-container {
  flex-shrink: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  /* 12 px es el mínimo de separación entre objetivos táctiles (norma 9). */
  gap: 12px;
  padding-top: 8px;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0));
}

/* Cabecera del paso: volver a la izquierda y la pregunta centrada, sin que el botón desplace
   el centro del título. */
/* Tres columnas con la misma reserva a los dos lados: es lo que garantiza que la flecha y el
   título no se pisen, pase lo que pase con la longitud del título. */
.reason-head {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) 48px;
  align-items: center;
  min-height: 48px;
  margin-bottom: 8px;
}

/* Solo la flecha, con 44 px de zona de toque. Con la etiqueta "Volver" al lado el botón medía
   92 px y el título, centrado sobre todo el ancho, se le montaba encima (visto a 1114×614). */
.reason-back {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--bz-p-gray-500);
  font-size: 20px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.reason-title {
  margin: 0;
  text-align: center;
  font-size: 17px;
  font-weight: 700;
  color: var(--bz-p-1a1a2e);
}

/* El icono no decora: con tres frases parecidas es lo que se distingue antes de leer, así que va
   alineado a la izquierda como carril y el texto arranca siempre en el mismo punto. */
.btn-reason {
  min-height: 56px;
  padding: 10px 16px 10px 10px;
  font-size: 16px;
  font-weight: 500;
  background: none;
  color: var(--bz-p-gray-700);
  border: 1.5px solid var(--bz-p-gray-300);
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  text-align: left;
}

/* La ficha le da cuerpo al icono para que no se lea como un carácter más de la frase.
   El color no es nuevo ni significa nada: es la misma receta de «contenedor neutro» que ya usa
   `.warning-box` en este mismo modal. El único acento de la pantalla sigue siendo el rojo de la
   cabecera, que es el veredicto (norma 11: cada color, un trabajo). */
.reason-chip {
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--bz-surface--slate-100);
  border: 1px solid var(--bz-p-slate-200);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* El icono habla con la misma voz que su frase: a #6B7280 pesaba menos que el texto y por eso
   «parecía texto». */
.reason-chip .pi {
  font-size: 20px;
  color: var(--bz-p-gray-700);
  line-height: 1;
}

/* "Cerrar" existe para que la pregunta no sea un peaje: se entrega igual. Va más apagada porque
   no es la respuesta que buscamos, pero tiene que estar — y sin ficha y centrado, para que sea de
   otra especie que las tres respuestas y no se pulse por inercia. */
.btn-reason--skip {
  display: flex;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 10px 16px;
  font-size: 15px;
  color: var(--bz-p-gray-500);
  border-color: var(--bz-p-gray-200);
}

.btn-reason--skip .pi {
  font-size: 17px;
}

@media (min-width: 768px) {
  .reason-title { font-size: 21px; }
  /* La flecha, no una etiqueta: en tablet crece como el resto, no al revés. */
  .reason-back { font-size: 22px; }
  /* Mismo criterio que los dos botones de arriba: alto y cuerpo de tablet, no de móvil. */
  .btn-reason {
    min-height: 68px;
    font-size: 21px;
    padding: 10px 18px 10px 12px;
    grid-template-columns: 44px minmax(0, 1fr);
    gap: 14px;
  }
  .reason-chip {
    width: 44px;
    height: 44px;
    border-radius: 10px;
  }
  .reason-chip .pi { font-size: 24px; }
  .btn-reason--skip {
    min-height: 56px;
    padding: 10px 18px;
    font-size: 18px;
  }
  .btn-reason--skip .pi { font-size: 18px; }
}

@media (min-width: 768px) {
  .cta-container {
    gap: 12px;
    padding-top: 20px;
    padding-bottom: 0;
  }
}

/* ========================================
   COMPACT STATE — loading, error, perfect
   ======================================== */
.overlay-compact {
  align-items: center;
}

.popup-compact {
  height: auto;
  max-height: calc(100dvh - 72px);
  border-radius: 20px;
  width: calc(100% - 48px);
}

@media (min-width: 768px) {
  .popup-compact {
    width: min(85vw, 400px);
    max-height: 92vh;
  }
}

@media (min-width: 1024px) {
  .popup-compact {
    width: min(80vw, 420px);
    max-height: 88vh;
  }
}

.popup-compact .state-center {
  padding: 20px 0;
}

/* ════ The verdict on the design system (src/ui/lane.js: `ui-system-lane`, per restaurant) ════
   Dark, like every other interruption of the system (Adrián, 8-oct-2026): the raised surface, its edge,
   radius 20 and the short shadow, centred on any screen and as tall as its content, over a flat scrim
   (no blur: docs/sistema-diseno/forma-y-tamano.md, «Veredicto»). «Incorrect» speaks in the palette's
   bronze orange (paleta-bronze.md), correct in its green; the missing products in ONE group with
   hairline dividers, not a card per product; the two answers at 72 with the radius of a 72 button and a
   hint at 14. Production's rules above stay for the stores without the flag. */
:root[data-bz-lane="system"] .analysis-popup-overlay {
  align-items: center;
  padding: var(--bz-gutter);
  box-sizing: border-box;
  background: var(--bz-scrim);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

:root[data-bz-lane="system"] .analysis-popup {
  box-sizing: border-box;
  width: min(100%, 440px);
  height: auto;
  max-height: 100%;
  padding: var(--bz-space-6);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-xl);
  background: var(--bz-raised);
  color: var(--bz-ink);
  box-shadow: var(--bz-shadow-overlay);
  font-family: var(--bz-font);
}

:root[data-bz-lane="system"] .analysis-popup.popup-reasons {
  width: min(100%, var(--bz-dialog-width));
}

:root[data-bz-lane="system"] .popup-compact {
  width: min(100%, 420px);
}

/* Loading, error and the perfect order. */
:root[data-bz-lane="system"] .popup-title {
  margin: 0 0 var(--bz-space-2);
  color: var(--bz-ink);
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  font-weight: var(--bz-weight-label);
}

/* The same title as the verdict that something is missing (22/600): what says «correct» at a glance is
   the green, the 72 icon and the word, not a bigger size for one outcome (13-oct-2026, one size per role). */
:root[data-bz-lane="system"] .title-success {
  margin-top: var(--bz-space-1);
  color: var(--bz-ok);
}

:root[data-bz-lane="system"] .state-icon {
  width: 1em;
  height: 1em;
  margin-bottom: var(--bz-space-3);
  font-size: var(--bz-target-md);
}

:root[data-bz-lane="system"] .state-icon--ok {
  color: var(--bz-ok);
  font-size: var(--bz-target-primary);
}

:root[data-bz-lane="system"] .state-icon--error {
  color: var(--bz-ink-2);
}

:root[data-bz-lane="system"] .loading-spinner {
  color: var(--bz-action-ink);
}

/* A status line under the title, not a second title: a dialog's body, 18, at a label's weight. */
:root[data-bz-lane="system"] .upload-count {
  color: var(--bz-ink);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .divider {
  margin: var(--bz-space-4) 0;
  background: var(--bz-edge);
}

:root[data-bz-lane="system"] .error-message {
  margin: var(--bz-space-2) 0 var(--bz-space-6);
  color: var(--bz-ink-2);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
}

:root[data-bz-lane="system"] .warning-box {
  box-sizing: border-box;
  margin: var(--bz-space-4) 0;
  padding: var(--bz-space-4);
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-surface);
}

:root[data-bz-lane="system"] .warning-icon {
  margin-bottom: var(--bz-space-2);
  color: var(--bz-ink-2);
  font-size: var(--bz-icon-lg);
}

:root[data-bz-lane="system"] .warning-text {
  color: var(--bz-ink);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
}

/* A progress bar says how much is left: the system's thin track and its active blue; the perfect
   order's fills with the compositor (scaleX), not with `width`. */
:root[data-bz-lane="system"] .progress-bar {
  height: var(--bz-slider-track);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
}

:root[data-bz-lane="system"] .progress-fill {
  border-radius: var(--bz-radius-full);
  background: var(--bz-track-active);
}

:root[data-bz-lane="system"] .fill-success {
  width: 100%;
  background: var(--bz-ok);
  transform: scaleX(0);
  transform-origin: left center;
}

:root[data-bz-lane="system"] .fill-success.progress-animate {
  animation: fillProgressScale 2s ease-out forwards;
}

@keyframes fillProgressScale {
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
}

/* Missing products: the header. */
:root[data-bz-lane="system"] .alert-header {
  gap: var(--bz-space-3);
  padding: 0 0 var(--bz-space-5);
}

:root[data-bz-lane="system"] .alert-icon {
  color: var(--bz-wrong);
  font-size: var(--bz-icon-lg);
}

/* The title in the ink, the brightest pair there is (16:1 on what floats): «incorrect» is said by the
   icon's orange beside it and by the words. In orange the title read faint on the dark card (Adrián,
   13 and 14-oct-2026, even at 6:1). */
:root[data-bz-lane="system"] .alert-title {
  color: var(--bz-ink);
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .alert-subtitle {
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
}

:root[data-bz-lane="system"] .alert-summary {
  color: var(--bz-ink);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  font-weight: var(--bz-weight-body);
}

/* The products: one card, a hairline between them. */
:root[data-bz-lane="system"] .items-wrapper {
  width: 100%;
  margin: 0;
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-lg);
  background: var(--bz-surface);
}

/* The fade at the foot says «there is more»: only while there is, or it greys the last product. */
:root[data-bz-lane="system"] .items-wrapper::after {
  display: none;
  background: linear-gradient(to bottom, transparent, var(--bz-surface));
}

:root[data-bz-lane="system"] .items-wrapper.has-more::after {
  display: block;
}

/* The list scrolls inside the card. On a phone its `max-height: 100%` never resolved: the wrapper's
   height is the flex column's, not one set, so on a phone seven products were cut at the card's foot with
   no scroll (14-oct-2026; docs/sistema-diseno/forma-y-tamano.md, «Veredicto»). The wrapper is a column
   itself now, and the list a shrinking item of it: the flex gives it the height to scroll in. */
:root[data-bz-lane="system"] .items-wrapper {
  display: flex;
  flex: 0 1 auto;
  flex-direction: column;
}

:root[data-bz-lane="system"] .items-list {
  flex: 0 1 auto;
  min-height: 0;
  padding: 0;
  scrollbar-color: var(--bz-outline) transparent;
}

/* The tablet keeps its ceiling (min(50vh, 400px), production's): the phone's was the 100% that did not
   resolve. */
@media (max-width: 767px) {
  :root[data-bz-lane="system"] .items-list {
    max-height: none;
  }
}

:root[data-bz-lane="system"] .missing-item {
  margin: 0;
  padding: var(--bz-space-3) var(--bz-space-4);
  border: 0;
  border-bottom: 1px solid var(--bz-edge);
  border-radius: 0;
  background: none;
}

:root[data-bz-lane="system"] .missing-item:last-child {
  border-bottom: 0;
}

:root[data-bz-lane="system"] .item-name {
  color: var(--bz-ink);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  font-weight: var(--bz-weight-label);
}

/* What is missing, in «incorrect»'s luminous tag at the end of its row: read at a glance, so the solid (the
   same as «Haz la foto a…» over the camera), never the dark container that glare greys. */
:root[data-bz-lane="system"] .item-qty {
  padding: var(--bz-space-1) var(--bz-space-3);
  border-radius: var(--bz-radius-full);
  background: var(--bz-wrong-solid);
  color: var(--bz-on-wrong-solid);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
  letter-spacing: var(--bz-tracking-sm);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .sub-item-text {
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
}

:root[data-bz-lane="system"] .item-info-icon,
:root[data-bz-lane="system"] .item-info-text {
  color: var(--bz-ink-2);
}

:root[data-bz-lane="system"] .item-info-icon {
  font-size: var(--bz-icon-xs);
}

:root[data-bz-lane="system"] .item-info-text {
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
}

:root[data-bz-lane="system"] .scroll-hint {
  border: 1px solid var(--bz-edge);
  border-radius: var(--bz-radius-full);
  background: var(--bz-control);
  color: var(--bz-ink);
  box-shadow: none;
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
}

/* The two answers: 72 with the radius of a 72 button and an 18/600 label. */
:root[data-bz-lane="system"] .cta-container {
  gap: var(--bz-target-gap);
  padding: var(--bz-space-5) 0 0;
}

:root[data-bz-lane="system"] .btn {
  border-radius: var(--bz-radius-lg);
  font-family: var(--bz-font);
}

:root[data-bz-lane="system"] .btn-primary {
  height: var(--bz-target-primary);
  gap: var(--bz-space-3);
  background: var(--bz-action);
  color: var(--bz-on-action);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .btn-icon {
  font-size: var(--bz-icon-md);
}

:root[data-bz-lane="system"] .btn-secondary {
  height: var(--bz-target-primary);
  gap: 0;
  border: 1px solid var(--bz-edge);
  background: var(--bz-control);
}

:root[data-bz-lane="system"] .btn-secondary-label {
  color: var(--bz-ink);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  font-weight: var(--bz-weight-label);
}

/* «The customer can claim»: read to decide, so 14 and never less (norm 9). */
:root[data-bz-lane="system"] .btn-secondary-warning {
  color: var(--bz-ink-2);
  font-size: var(--bz-text-sm);
  line-height: var(--bz-leading-sm);
}

:root[data-bz-lane="system"] .btn-ghost {
  height: var(--bz-target-md);
  border: 1px solid var(--bz-outline);
  background: none;
  color: var(--bz-ink);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  font-weight: var(--bz-weight-label);
}

/* The second question of «deliver without correcting». */
:root[data-bz-lane="system"] .reason-back {
  border-radius: var(--bz-radius-full);
  color: var(--bz-ink-2);
  font-size: var(--bz-icon-md);
}

:root[data-bz-lane="system"] .reason-title {
  color: var(--bz-ink);
  font-size: var(--bz-text-xl);
  line-height: var(--bz-leading-xl);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .btn-reason {
  min-height: var(--bz-target-primary);
  padding: 0 var(--bz-space-4) 0 var(--bz-space-3);
  border: 1px solid var(--bz-edge);
  background: var(--bz-control);
  color: var(--bz-ink);
  font-size: var(--bz-text-lg);
  line-height: var(--bz-leading-lg);
  font-weight: var(--bz-weight-label);
}

:root[data-bz-lane="system"] .reason-chip {
  width: var(--bz-target-min);
  height: var(--bz-target-min);
  border: 0;
  border-radius: var(--bz-radius-md);
  background: var(--bz-surface);
  color: var(--bz-action-ink);
  font-size: var(--bz-icon-md);
}

:root[data-bz-lane="system"] .btn-reason--skip {
  min-height: var(--bz-target-min);
  border: 0;
  background: none;
  color: var(--bz-ink-2);
  font-size: var(--bz-text-md);
  line-height: var(--bz-leading-md);
  font-weight: var(--bz-weight-label);
}

/* An error, on a phone: the whole screen, as TimeoutPopupComponent's (14-oct-2026). */
@media (max-width: 767px) {
  :root[data-bz-lane="system"] .analysis-popup-overlay.is-error {
    padding: 0;
    background: var(--bz-bg);
  }

  :root[data-bz-lane="system"] .analysis-popup-overlay.is-error .analysis-popup {
    width: 100%;
    height: 100%;
    padding: calc(var(--bz-gutter) + env(safe-area-inset-top, 0px)) var(--bz-gutter) calc(var(--bz-gutter) + env(safe-area-inset-bottom, 0px));
    border: 0;
    border-radius: 0;
    background: var(--bz-bg);
    box-shadow: none;
  }

  /* The words in the middle of what is left, Cerrar at the bottom, as the timeout's. */
  :root[data-bz-lane="system"] .analysis-popup-overlay.is-error .state-center {
    justify-content: flex-start;
  }

  :root[data-bz-lane="system"] .analysis-popup-overlay.is-error .state-icon {
    margin-top: auto;
  }

  :root[data-bz-lane="system"] .analysis-popup-overlay.is-error .btn-primary {
    align-self: stretch;
    margin-top: auto;
  }
}

@media (min-width: 768px) {
  :root[data-bz-lane="system"] .btn-reason {
    grid-template-columns: var(--bz-target-min) minmax(0, 1fr);
  }
}
</style>